import React, { useState, useEffect, useContext } from "react";
import { Button, Popover, Drawer, message, Space, Modal, Divider } from "antd";
import { useLocation } from "react-router-dom";
import { Link } from "react-router-dom";
import DataTable, { pageSizeKey } from "../../helper/dataTable/dataTable";
import { saveAs } from "file-saver";
import {
  SearchOutlined,
  MoreOutlined,
  LinkOutlined,
  PrinterOutlined,
  FilePdfOutlined,
  FileExcelOutlined,
} from "@ant-design/icons";
import Printing from "../../helper/Print/Printing";
import {
  getItemMovmentReport,
  getItemMovementHtml,
  getItemMovmentXls,
  getItemMovmentPdf,
} from "../../../services/Reports/ReportService";
import _t from "../../../languages/translate";
import FilterItemMovementRep from "../../helper/Modal/FilterItemMovementRep";
import { LanguageContext } from "../../../languages/Language";

const ItemMovmentReport = () => {
  let location = useLocation();
  const { userLanguage } = useContext(LanguageContext);
  const [dataSource, setDataSource] = useState([]);
  const [columns, setColumns] = useState([]);
  const [meta, setMeta] = useState({});
  const [html, setHtml] = useState("");
  const [loadData, setLoadData] = useState(false);
  const [visible, setVisible] = useState(false);
  const [reportFilter, setReportFilter] = useState({});
  const [visiblePrDrawer, setVisiblePrDrawer] = useState(false);
  const [objSearchState, setObjSearchState] = useState(null);
  const savedPageSize = localStorage.getItem(pageSizeKey);

  const handelLinkDataObj = () => {
    let dataObj = JSON.parse(localStorage.getItem("linkObj"));
    setObjSearchState(dataObj);
    localStorage.removeItem("linkObj");
  };

  useEffect(() => {
    const fetchData = async () => {
      if (localStorage.getItem("linkObj") !== null) {
        handelLinkDataObj();
      }

      const query = new URLSearchParams(location.search);
      if (savedPageSize && !query.has(pageSizeKey))
        query.set(pageSizeKey, savedPageSize);
      try {
        setLoadData(true);

        if (objSearchState !== null) {
          const { data: responce } = await getItemMovmentReport(
            query.toString(),
            objSearchState
          );
          if (responce.data.dataSource.length > 0) {
            setColumns(responce.data.columns);
            setDataSource(responce.data.dataSource);
            setReportFilter(responce.data.reportFilter);
            setMeta(responce.meta);
          } else {
            setDataSource([]);
            message.info(_t("msgThereIsNoData"), 3);
          }
        }
      } catch (error) {
        if (error.response) {
          message.error(
            error.response.data?.message || _t("msgUnknownError"),
            3
          );
        } else if (error.request) {
          message.error(_t("msgServerDisconnected"), 3);
        } else {
          message.error(_t("msgUnknownError"), 3);
        }
      } finally {
        setLoadData(false);
      }
    };

    fetchData();
  }, [
    location.search,
    savedPageSize,
    objSearchState,
    setDataSource,
    setMeta,
    setLoadData,
  ]);

  const onClosePrDrawer = () => {
    setVisiblePrDrawer(false);
  };

  const showDrawer = () => {
    setVisible(true);
  };

  const onClose = () => {
    setVisible(false);
  };

  //**.start >> Drawing links ********************
  const linksColumns = [
    {
      key: "reportTempMenu",
      align: "center",
      title: <LinkOutlined />,
      width: 50,
      fixed: "left",
      render: (item) => {
        return item.reportTempMenu.length !== 0 ? (
          <Popover placement="bottomRight" content={() => linkCont(item)}>
            <Button
              type="link"
              icon={<MoreOutlined style={{ color: "#3a7df2", fontSize: 16 }} />}
            />
          </Popover>
        ) : undefined;
      },
    },
  ];

  const linkCont = (item) => (
    <div>
      {item.reportTempMenu.map((e) =>
        e.type === 0 ? (
          <p key={e.key}>
            <Link target="_blank" rel="noopener noreferrer" to={e.url}>
              <label style={{ cursor: "pointer" }}>{e.name}</label>
            </Link>
          </p>
        ) : (
          <p key={e.key}>
            <Link
             target="_blank"
              rel="noopener noreferrer"
              to={e.url}
              onClick={() => handelLinkObj(item, e.reportKey)}
            >
              <label style={{ cursor: "pointer" }}>{e.name}</label>
            </Link>
          </p>
        )
      )}
    </div>
  );

  const handelLinkObj = (item, repKey) => {
    reportFilter.itemIds = [item.itemId];
    let obj = {
      reportFilter: reportFilter,
      row: JSON.stringify(item),
      reportKey: repKey,
      timeShift: new Date().getTimezoneOffset(),
    };
   
    localStorage.setItem("linkObj", JSON.stringify(obj));
  };
  //**.end >> Drawing links ********************

  const handelItemMovementRep = async (data) => {
    setLoadData(true);
    data.columns = columnsToObject();
    setObjSearchState(data);
    setVisible(false);
  };

  const columnsToObject = () => {
    const savedColumns = localStorage.getItem("itemMovmentRep");
    const parsedColumns = savedColumns && JSON.parse(savedColumns);
    const filteredColumns = Array.isArray(parsedColumns)
      ? parsedColumns.filter(
        (c) => c.key !== "edit" && c.key !== "reportTempMenu"
      )
      : columns;
    return filteredColumns;
  };

  const onPrint = async (obj) => {
    try {
      obj.columns = columnsToObject();
      const query = new URLSearchParams(location.search);

      const data = await getItemMovementHtml(
        query.toString(),
        obj,
        userLanguage === "ar" ? 0 : 1
      );

      setHtml(data.data);
      setVisiblePrDrawer(true);
    } catch (error) {
      if (error.response) {
        message.error(error.response.data?.message || _t("msgUnknownError"), 3);
      } else if (error.request) {
        message.error(_t("msgServerDisconnected"), 3);
      } else {
        message.error(_t("msgUnknownError"), 3);
      }
    } finally {
      setLoadData(false);
    }
  };

  const onExportToXls = async (obj) => {
    const hide = message.loading(_t("strDataExporting"), 0);
    try {
      obj.columns = columnsToObject();
      const query = new URLSearchParams(location.search);

      const { data, headers } = await getItemMovmentXls(
        query.toString(),
        obj,
        userLanguage === "ar" ? 0 : 1
      );
      const byteCharacters = atob(data.data);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      saveAs(blob, "ItemMovementReport.xlsx");
    } catch (error) {
      if (error.response) {
        message.error(error.response.data?.message || _t("msgUnknownError"), 3);
      } else if (error.request) {
        message.error(_t("msgServerDisconnected"), 3);
      } else {
        message.error(_t("msgUnknownError"), 3);
      }
    } finally {
      setTimeout(hide);
    }
  };

  const onExportToPDF = async (obj) => {
    const hide = message.loading(_t("strDataExporting"), 0);
    try {
      obj.columns = columnsToObject();
      const query = new URLSearchParams(location.search);

      const { data, headers } = await getItemMovmentPdf(
        query.toString(),
        obj,
        userLanguage === "ar" ? 0 : 1
      );
      const byteCharacters = atob(data.data);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: "application/pdf" });
      saveAs(blob, "ItemMovementReport.pdf");

      console.log(data);
    } catch (error) {
      if (error.response) {
        message.error(error.response.data?.message || _t("msgUnknownError"), 3);
      } else if (error.request) {
        message.error(_t("msgServerDisconnected"), 3);
      } else {
        message.error(_t("msgUnknownError"), 3);
      }
    } finally {
      setTimeout(hide);
    }
  };

  return (
    <>
      <Drawer
        width={720}
        onClose={onClosePrDrawer}
        visible={visiblePrDrawer}
        title={_t("strPrintPreview")}
      >
        <Printing htmlRep={html} />
      </Drawer>
      <Modal
        title={_t("strItemMovementReport")}
        visible={visible}
        onCancel={() => onClose()}
        footer={null}
        width={800}
      >
        <FilterItemMovementRep
          onFilterData={objSearchState?.reportFilter !== null ? objSearchState?.reportFilter : undefined}
          onHandelData={(data) => handelItemMovementRep(data)}
        />
      </Modal>
      <DataTable
        loading={loadData}
        hideSearch
        dataSource={dataSource}
        columns={[...linksColumns, ...columns]}
        saveColumnsProps="itemMovmentRep"

        rowClassName={(record, index) =>
          record.rowType === "First" ? "firstActive-row" :
            record.rowType === "Total" ? "active-row" :
              record.rowType === "IN" ? "IN-row" :
                record.rowType === "OUT" ? "OUT-row" :
                  record.rowType === "TRANS" ? "TRANS-row" :
                    index % 2 === 0
                      ? "table-row-light"
                      : "table-row-dark"
        }


        meta={meta}
        bordered
        extra={
          <Space size="small">
            <Button
              size="small"
              type="primary"
              onClick={showDrawer}
              style={{ width: 150, borderRadius: 5 }}
              icon={<SearchOutlined />}
            >
              {_t("strSearch")}
            </Button>
            <Divider type="vertical" />
            <Button
              onClick={() => onPrint(objSearchState)}
              size="small"
              type="link"
              style={{
                width: 100,
                borderRadius: 5,
              }}
              icon={<PrinterOutlined />}
              disabled={dataSource.length !== 0 ? false : true}
            >
              {" "}
              {_t("strPrint")}
            </Button>
            <Divider type="vertical" />
            <Button
              size="small"
              onClick={() => {
                onExportToXls(objSearchState);
              }}
              type="link"
              style={{ width: 100, borderRadius: 5 }}
              disabled={dataSource.length !== 0 ? false : true}
              icon={<FileExcelOutlined style={{ fontSize: 16 }} />}
            >
              {_t("strExportToExcel")}
            </Button>
            <Divider type="vertical" />
            <Button
              size="small"
              onClick={() => {
                onExportToPDF(objSearchState);
              }}
              type="link"
              style={{ width: 100, borderRadius: 5 }}
              disabled={dataSource.length !== 0 ? false : true}
              icon={<FilePdfOutlined style={{ fontSize: 16 }} />}
            >
              {_t("strExportToPDF")}
            </Button>
          </Space>
        }
      />
    </>
  );
};

export default ItemMovmentReport;
